import { db, auth } from "../../services/firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  setDoc,
  getDoc,
  Timestamp
} from "firebase/firestore";    

export async function submitBookingFeedback({booking , feedbackType, customMessage, userType}){

    if (!booking?.id || !booking?.itemName || !booking?.startTime) {
        throw new Error("Invalid booking data");
    }

    if (!feedbackType) {
        throw new Error("feedbackType is required");
    }
    const user = auth.currentUser;

    await addDoc(collection(db, 'bookingFeedback'), {
    bookingId: booking.id,
    serviceName: booking.itemName,
    bookingDate: booking.startTime,
    feedbackType,
    customMessage: customMessage || '',
    submittedBy: userType,
    submittedAt: new Date(),
    userId: user?.uid,
    userEmail: user?.email,
    status: 'unread'
    });    
}

export async function getProviderBookingsClient({providerId, debugLabel = null}) {
  const startedAt = performance.now();
  const bookingsRef = collection(db, 'providers', providerId, 'bookings');
  const snapshot = await getDocs(bookingsRef);
  const bookings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  if (debugLabel) {
    console.groupCollapsed(`[FirestoreReads:${debugLabel}] getProviderBookings`);
    console.table({
      providerId,
      bookingDocReads: snapshot.size,
      estimatedTotalReads: snapshot.size,
      elapsedMs: Math.round(performance.now() - startedAt)
    });
    console.groupEnd();
  }

  return bookings.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
}

export async function updateBookingStatusClient({ booking, newStatus, providerId }) {
  const bookingId = booking.bookingId || booking.id;

  await Promise.all([
    updateDoc(doc(db, 'bookings', bookingId), { status: newStatus }),

    updateDoc(doc(db, 'services', booking.serviceId, 'bookings', bookingId), {
      status: newStatus
    }),

    updateDoc(doc(db, 'users', booking.userId, 'bookings', bookingId), {
      status: newStatus
    }),

    updateDoc(doc(db, 'providers', providerId, 'bookings', bookingId), {
      status: newStatus
    })
  ]);
}

export async function getServiceBookingsClient({ serviceId }) {
  const bookingsRef = collection(db, 'services', serviceId, 'bookings');
  const snapshot = await getDocs(bookingsRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// Returns the most relevant consultation booking for a given user+serviceId+serviceItemId
export async function getUserConsultationStatusClient({ uid, serviceId, serviceItemId }) {
  const bookingsRef = collection(db, 'users', uid, 'bookings');
  const snapshot = await getDocs(bookingsRef);
  const consultations = snapshot.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(b =>
      b.bookingType === 'consultation' &&
      b.serviceId === serviceId &&
      b.consultationForServiceItemId === serviceItemId
    );
  // Priority: completed > active (pending/confirmed) > anything else
  const completed = consultations.find(b => b.status === 'completed');
  if (completed) return { status: 'completed', booking: completed };
  const active = consultations.find(b => ['pending', 'confirmed'].includes(b.status));
  if (active) return { status: active.status, booking: active };
  return null;
}

// export async function createBookingClient(bookingData) {
//   console.log('createBookingClient', bookingData);
//   const user = auth.currentUser;
//   if (!user) throw new Error('User not authenticated');
  
//   const bookingNumber = '#' + Math.random().toString(36).substr(2, 6).toUpperCase();
//   const createdAt = Timestamp.now();
  
//   const fullBookingData = {
//     ...bookingData,
//     userId: user.uid,
//     bookingNumber,
//     status: 'pending',
//     createdAt
//   };
  
//   // Get service data to find providerId
//   const serviceDoc = await getDoc(doc(db, 'services', bookingData.serviceId));
//   if (!serviceDoc.exists()) throw new Error('Service not found');
  
//   const providerId = serviceDoc.data().providerId;
  
//   // Create booking with auto-generated ID
//   const bookingRef = doc(collection(db, 'services', bookingData.serviceId, 'bookings'));
//   const bookingId = bookingRef.id;
  
//   const finalBookingData = {
//     ...fullBookingData,
//     bookingId
//   };
  
//   // Save booking in all three collections
//   await Promise.all([
//     // Services collection
//     setDoc(bookingRef, finalBookingData),
//     // Providers collection
//     setDoc(doc(db, 'providers', providerId, 'bookings', bookingId), {
//       ...finalBookingData,
//       providerId,
//       itemName: bookingData.item.name
//     }),
//     // Users collection
//     setDoc(doc(db, 'users', user.uid, 'bookings', bookingId), {
//       ...finalBookingData,
//       providerId,
//       itemName: bookingData.item.name
//     })
//   ]);
  
//   return { providerId, bookingNumber };
// }

export async function createBookingClient(bookingData) {
  console.log('createBookingClient', bookingData);

  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const bookingNumber = '#' + Math.random().toString(36).substr(2, 6).toUpperCase();
  const createdAt = Timestamp.now();

  // Get service data
  const serviceDoc = await getDoc(doc(db, 'services', bookingData.serviceId));
  if (!serviceDoc.exists()) throw new Error('Service not found');

  const serviceData = serviceDoc.data();
  const providerId = serviceData.providerId;

  // Optional: get user data for name
  const userDoc = await getDoc(doc(db, 'users', user.uid));
  const userData = userDoc.exists() ? userDoc.data() : {};

  // Optional: get provider data
  const providerDoc = await getDoc(doc(db, 'providers', providerId));
  const providerData = providerDoc.exists() ? providerDoc.data() : {};

  // Create booking with auto-generated ID
  const bookingRef = doc(collection(db, 'services', bookingData.serviceId, 'bookings'));
  const bookingId = bookingRef.id;

  const finalBookingData = {
    ...bookingData,

    bookingId,
    userId: user.uid,
    providerId,
    serviceId: bookingData.serviceId,

    bookingNumber,
    status: 'pending',
    createdAt,

    itemName: bookingData.item?.name || bookingData.itemName || 'Booking',

    userName:
      userData.firstName ||
      user.displayName ||
      'Customer',

    userEmail: user.email || userData.email || null,

    providerName:
      serviceData.name ||
      providerData.name ||
      'Provider',

    providerEmail:
      serviceData.businessEmail ||
      providerData.email ||
      null,

    serviceName: serviceData.name || null,

    reminders: {
      dayBeforeCustomerSentAt: null,
      dayBeforeProviderSentAt: null,
      dayOfCustomerSentAt: null,
      dayOfProviderSentAt: null,
      customerFeedbackSentAt: null,
      providerFeedbackSentAt: null,
    },
  };

  await Promise.all([
    // New top-level collection for reminders/emails/admin jobs
    setDoc(doc(db, 'bookings', bookingId), finalBookingData),

    // Existing service booking copy
    setDoc(bookingRef, finalBookingData),

    // Existing provider booking copy
    setDoc(doc(db, 'providers', providerId, 'bookings', bookingId), finalBookingData),

    // Existing user booking copy
    setDoc(doc(db, 'users', user.uid, 'bookings', bookingId), finalBookingData),
  ]);

  return { providerId, bookingNumber, bookingId };
}
